import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useLang } from "@/i18n";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Motion";
import { useSeo } from "@/hooks/useSeo";

const API = process.env.REACT_APP_BACKEND_URL || "";

const assetUrl = (url) => {
  if (!url || /^https?:\/\//i.test(url) || url.startsWith("data:")) return url;
  return `${API}${url.startsWith("/") ? url : `/${url}`}`;
};

const rewriteAssetUrls = (html) =>
  (html || "").replace(/(<img\b[^>]*\bsrc=["'])(\/api\/[^"']+)(["'])/gi, (_, before, src, after) => `${before}${assetUrl(src)}${after}`);

const fmtDate = (iso, lang) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(lang === "es" ? "es-ES" : "en-US", { day: "numeric", month: "long", year: "numeric" });
};

const readingMinutes = (html) => {
  const words = (html || "").replace(/<[^>]+>/g, " ").split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
};

export default function BlogPost() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const b = t.blog;
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPost(null);
    setError(false);
    fetch(`${API}/api/blog/posts/${slug}?lang=${lang}`)
      .then((r) => {
        if (!r.ok) throw new Error("not found");
        return r.json();
      })
      .then((data) => !cancelled && setPost(data))
      .catch(() => !cancelled && setError(true));
    fetch(`${API}/api/blog/posts?lang=${lang}`)
      .then((r) => r.json())
      .then((list) => !cancelled && setRelated(list.filter((p) => p.slug !== slug).slice(0, 3)))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [slug, lang]);

  const content = useMemo(() => (post ? rewriteAssetUrls(post.content) : ""), [post]);
  const minutes = useMemo(() => (post ? readingMinutes(content) : 0), [post, content]);

  useSeo({
    title: post?.seo?.[lang]?.title || post?.title,
    description: post?.seo?.[lang]?.description || post?.excerpt,
    image: assetUrl(post?.image),
    lang,
    type: "article",
  });

  return (
    <div data-testid="blog-post-page" className="min-h-screen bg-[#FAFAFD] text-[#353535]">
      <Navbar accent="#4361ee" />

      <article className="px-5 sm:px-8 pt-32 sm:pt-40 pb-20">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-2 text-sm font-jakarta font-semibold text-[#353535]/60 hover:text-[#353535] transition-colors">
              <ArrowLeft size={15} /> {b.back}
            </Link>
            {error && <p data-testid="blog-post-error" className="mt-10 text-sm font-jakarta text-[#ca3b42]">{b.loadError}</p>}
            {post && (
              <>
                <div className="mt-10 font-jbmono text-[11px] uppercase tracking-[0.3em] text-[#4361ee]">{post.categories[0] || "Blog"}</div>
                <h1 data-testid="blog-post-title" className="mt-4 font-playfair text-4xl sm:text-5xl leading-tight">{post.title}</h1>
                <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-1 font-jbmono text-[11px] uppercase tracking-[0.15em] text-[#999999]">
                  {post.author && <span>{post.author}</span>}
                  {post.author && <span aria-hidden="true">·</span>}
                  <span>{fmtDate(post.date, lang)}</span>
                  <span aria-hidden="true">·</span>
                  <span>{minutes} {b.minRead}</span>
                </div>
                {post.image && (
                  <div className="mt-10 overflow-hidden rounded-3xl">
                    <img src={assetUrl(post.image)} alt={post.title} className="aspect-[16/9] w-full object-cover" />
                  </div>
                )}
                <div data-testid="blog-post-content" className="blog-content mt-10" dangerouslySetInnerHTML={{ __html: content }} />
              </>
            )}
          </Reveal>
        </div>
      </article>

      {related.length > 0 && (
        <section className="px-5 sm:px-8 pb-20">
          <div className="mx-auto max-w-3xl">
            <div className="font-jbmono text-[11px] uppercase tracking-[0.3em] text-[#4361ee] mb-6">{b.related}</div>
            <div className="grid gap-4 sm:grid-cols-3">
              {related.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`blog-related-${p.slug}`} className="group rounded-2xl border border-black/10 bg-white p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_rgba(67,97,238,0.10)]">
                  <div className="font-jbmono text-[10px] uppercase tracking-[0.2em] text-[#999999]">{p.categories[0] || "Blog"}</div>
                  <div className="mt-2 font-cabinet text-base font-bold leading-snug">{p.title}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="px-5 sm:px-8 pb-24 sm:pb-32">
        <Reveal className="mx-auto max-w-3xl rounded-3xl bg-gradient-to-br from-[#4361ee] to-[#3050d8] text-white p-10 sm:p-14 text-center">
          <h2 className="font-playfair text-2xl sm:text-4xl leading-tight">{b.ctaTitle}</h2>
          <p className="mt-4 text-sm sm:text-base font-jakarta text-white/75 max-w-md mx-auto">{b.ctaBody}</p>
          <Link to="/contact" data-testid="blog-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-jakarta font-bold text-[#353535] transition-transform duration-300 hover:scale-[1.04]">
            {b.cta} <ArrowRight size={16} />
          </Link>
        </Reveal>
      </section>

      <Footer />
    </div>
  );
}
